/**
 * The single definition of which platforms are public, on the React side.
 *
 * A public platform serves anonymous visitors on a public URL. That has three
 * consequences here, and each one used to carry its own copy of this list:
 *   - it never triggers an OAuth login redirect (useAppAuth.js)
 *   - it fetches config with a plain fetch, no bearer token (ApiHelpers.js)
 *   - it skips the community access check (App.js, AppSwitchboard.js) and is
 *     never served preview content (previewMode.js)
 *
 * Because it gates access, the list must agree with Drupal's — and in the same
 * order, since both sides are compared element-wise. Drupal's canonical copy is
 * `Drupal\community\PublicPlatforms::LIST`
 * (web/modules/custom/community/src/PublicPlatforms.php). A divergence gives a
 * platform the server serves anonymously but the client still gates, or the
 * reverse, and until this module existed nothing anywhere would have said so:
 * each repo's tests pinned its own side against its own copy.
 *
 * `publicPlatforms.drupal.json` is a vendored copy of the Drupal list, checked
 * by `npm run check:public-platforms` (CI). See that script's header for why it
 * is vendored rather than fetched.
 */
export const PUBLIC_PLATFORMS = Object.freeze([
  'page',
  'siteSee',
  'kiosk',
  'mapOnly',
  'mapTiles',
]);

/**
 * Whether a platform is public.
 *
 * @param {string|null|undefined} platform The platform from settings or the URL.
 * @returns {boolean} True if the platform skips access checks.
 */
export const isPublicPlatform = (platform) => PUBLIC_PLATFORMS.includes(platform);

export default PUBLIC_PLATFORMS;
